import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NotificationService } from '@cinetixx/cinetixx-ui';
import { finalize, forkJoin, Observable, of, Subject } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';
import { IDictionaryValue } from '@cinetixx/cinetixx-ui';
import { MIN_SECTOR_DIMENSIONS } from '../../constants';
import { IBaseElementPayload, IConfigurationPayload, ISeatsConnection } from '../../interfaces';
import { SectorManager } from '../../managers';
import { AuditoriumsApiService, SectorsApiService, SectorsConfigurationsApiService } from '../api-services';
import { ConnectionApiService } from '../api-services/ConnectionApiService';
import { WeightApiService } from '../api-services/WeightApiService';
import { CreatorCacheService } from './CreatorCacheService';
import { CreatorCurrentStepService } from './CreatorCurrentStepService';
import { CreatorStateService } from './CreatorStateService';
import { AuditoriumCreatorService } from './AuditoriumCreatorService';

@Injectable({
  providedIn: 'root'
})
export class CreatorSaverService {

  private readonly _sectorsApiService = inject(SectorsApiService);
  private readonly _notificationService = inject(NotificationService);
  private readonly _sectorsConfigurationsApiService = inject(SectorsConfigurationsApiService);
  private readonly _connectionApiService = inject(ConnectionApiService);
  private readonly _auditoriumsApiService = inject(AuditoriumsApiService);
  private readonly _creatorCurrentStepService = inject(CreatorCurrentStepService);
  private readonly _weightApiService = inject(WeightApiService);
  private readonly _auditoriumCreatorService = inject(AuditoriumCreatorService);

  public saveSeatsPlan(autoConnection: boolean): Observable<boolean> {
    const saved$ = new Subject<boolean>();

    CreatorStateService.isLoading = true;
    CreatorCacheService.makeSnapshot();
    const apiCalls$: Observable<void>[] = [];
    let noSaves = true;

    for (const [key, _] of CreatorCacheService.seatsPlansCacheMap) {
      if (!this._creatorCurrentStepService.isStepDirty(key)) {
        continue;
      }

      noSaves = false;

      const payload = this._creatorCurrentStepService.stepPayload<'seats-plan'>(key);

      if (typeof autoConnection === 'boolean') {
        payload.automaticConnection = autoConnection;
      }

      apiCalls$.push(this._sectorsApiService.saveSector(key, payload));
    }

    if (noSaves) {
      setTimeout(() => {
        saved$.next(true);
        saved$.complete();
      });
    } else {
      forkJoin(apiCalls$).pipe(
        tap(() => CreatorCacheService.shapesCache.clear()),
        switchMap(() => this._auditoriumCreatorService.getCreatorDataFromApi('shapes')),
        take(1)
      ).subscribe({
        next: () => {
          this._notificationService.pushSuccess(
            'Success'.trans('auditoriums/update-auditorium/notification/success.Title'),
            'The seatsplan has been saved successfully'.trans('auditoriums/update-auditorium/seatsplan/notification/success.Message')
          );

          saved$.next(true);
          saved$.complete();
        },
        error: (err: HttpErrorResponse) => {
          switch (err.error.Code) {
            case 'UsedSeat':
              this._notificationService.pushError(
                'Error'.trans('auditoriums/update-auditorium/notification/used-seat/error.Title'),
                'Cannot delete used seat'.trans('auditoriums/update-auditorium/notification/used-seat/error.Message')
              );
              break;
            default:
              this._notificationService.pushError(
                'Error'.trans('auditoriums/update-auditorium/notification/error.Title'),
                'An error occurred while saving the sector'.trans('auditoriums/update-auditorium/notification/error.Message')
              );
          }

          CreatorStateService.isLoading = false;
          saved$.next(false);
          saved$.complete();
        }
      });
    }

    return saved$.asObservable();
  }

  public saveSectorsPlan(): Observable<boolean> {
    const saved$ = new Subject<boolean>();
    CreatorStateService.isLoading = true;
    CreatorCacheService.makeSnapshot();

    const payload = this._creatorCurrentStepService.stepPayload<'sector-plan'>(CreatorStateService.seatsPlan.id);
    const elementsPayload = this._creatorCurrentStepService.elementsPayload(CreatorStateService.seatsPlan.id);
    let noActionsInvoked = false;

    const actions: {
      type: 'create' | 'delete' | 'changeName',
      data: IBaseElementPayload | number
    }[] = [];

    for (const data of elementsPayload.createElements) {
      actions.push({type: 'create', data});
    }

    for (const id of CreatorStateService.deleteElementsIds) {
      const fSectorId = CreatorStateService.initSeatsPlanState.sectors.find(s => s.id === id)?.sectorId;
      actions.push({type: 'delete', data: fSectorId});
    }

    for (const sector of CreatorStateService.initSeatsPlanState.sectors) {
      const fSector = payload.elements.find(x => x.sectorId === sector.sectorId);

      if (fSector) {
        if (sector.title !== fSector.title) {
          actions.push({type: 'changeName', data: fSector});
        }
      }
    }

    new Observable((observer) => {
      if (actions?.length) {
        let actionsCompleted = 0;

        const subCb = () => {
          actionsCompleted++;

          setTimeout(() => {
            if (actions.length === actionsCompleted) {
              observer.next();
              observer.complete();
            }
          });
        };

        for (let i = 0; i < actions.length; i++) {
          const action = actions[i];
          const type = action.type;
          const data = action.data;

          if (type === 'create') {
            this._auditoriumsApiService.createSector(
              CreatorStateService.seatsPlan.auditoriumId,
              SectorManager.getDataToSend({
                hasSectors: false,
                seatConfigurationId: (data as IBaseElementPayload).seatConfigurationId,
                x: 0,
                y: 0,
                w: MIN_SECTOR_DIMENSIONS.w,
                h: MIN_SECTOR_DIMENSIONS.h,
                webW: MIN_SECTOR_DIMENSIONS.w,
                webH: MIN_SECTOR_DIMENSIONS.h,
                name: (data as IBaseElementPayload).ruleTitle,
                parentSectorId: CreatorStateService.parentSectorId
              })
            ).subscribe((res) => {
              (data as IBaseElementPayload).sectorId = res.id;
              subCb();
            });
          } else if (type === 'delete') {
            this._sectorsApiService.deleteSector(data as number).subscribe(subCb.bind(this));
          } else if (type === 'changeName') {
            this._sectorsApiService.saveName(
              (data as IBaseElementPayload).sectorId,
              {name: (data as IBaseElementPayload).title}
            ).subscribe(subCb.bind(this));
          }
        }
      } else {
        noActionsInvoked = true;
        observer.next();
        observer.complete();
      }
    })
      .subscribe(() => {
        const invokeSave = () => {
          queueMicrotask(() => {
            this._sectorsApiService.saveSector(CreatorStateService.seatsPlan.id, {
              ...payload,
              elements: [...elementsPayload.createElements, ...elementsPayload.editElements]
            }).subscribe({
              next: () => {
                this._notificationService.pushSuccess(
                  'Success'.trans('auditoriums/update-sectors/notification/success.Title'),
                  'The sectors has been saved successfully'.trans('auditoriums/update-sectors/notification/success.Message')
                );
                saved$.next(true);
                saved$.complete();
              },
              error: () => {
                CreatorStateService.isLoading = false;
                this._notificationService.pushError(
                  'Error'.trans('auditoriums/update-sectors/notification/error.Title'),
                  'An error occurred while saving the sectors'.trans('auditoriums/update-sectors/notification/error.Message')
                );
                saved$.next(false);
                saved$.complete();
              }
            });
          });
        };

        if (noActionsInvoked) {
          if (this._creatorCurrentStepService.isStepDirty(CreatorStateService.seatsPlan.id)) {
            invokeSave();
          } else {
            setTimeout(() => {
              saved$.next(true);
              saved$.complete();
            });
          }
        } else {
          invokeSave();
        }
      });

    return saved$.asObservable();
  }

  public saveConnections(): Observable<boolean> {
    const saved$ = new Subject<boolean>();
    CreatorStateService.isLoading = true;
    CreatorCacheService.makeSnapshot();
    const data: ISeatsConnection[] = [];

    for (const [key, _] of CreatorCacheService.seatsPlansCacheMap) {
      data.push(...this._creatorCurrentStepService.stepPayload<'connections'>(key));
    }

    this._connectionApiService.setSeatsConnections(data)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this._notificationService.pushSuccess(
            'Success'.trans('auditoriums/update-auditorium/connections/notification/success.Title'),
            'Connections has been saved successfully'.trans('auditoriums/update-auditorium/connections/notification/success.Message')
          );
          saved$.next(true);
          saved$.complete();
        },
        error: () => {
          CreatorStateService.isLoading = false;
          this._notificationService.pushError(
            'Error'.trans('auditoriums/update-auditorium/connections/notification/error.Title'),
            'An error occurred while saving the connections'.trans('auditoriums/update-auditorium/connections/notification/error.Message')
          );
          saved$.next(false);
          saved$.complete();
        }
      });

    return saved$.asObservable();
  }

  public saveSeatPriority(): Observable<boolean> {
    const saved$ = new Subject<boolean>();
    let apiCalls$: Observable<void>[] = [];

    CreatorStateService.isLoading = true;
    CreatorCacheService.makeSnapshot();

    for (const [_, value] of CreatorCacheService.seatsPlansCacheMap) {
      const payload = this._creatorCurrentStepService.stepPayload<'seat-priority'>(value.seatsPlan.id);
      apiCalls$.push(this._weightApiService.setSeatsWeights(payload));
    }

    forkJoin(apiCalls$).pipe(
      take(1),
      switchMap(() => this._sectorsApiService.getOneSector(CreatorStateService.seatsPlan.id)),
      finalize(() => CreatorStateService.isLoading = false),
    ).subscribe({
      next: () => {
        this._notificationService.pushSuccess(
          'Priority saved'.trans('auditoriums/priority/save-priority/notifications/success.Title'),
          'You have successfully saved the priority.'.trans('auditoriums/priority/save-priority/notifications/success.Message')
        );

        saved$.next(true);
        saved$.complete();
      },
      error: () => {
        CreatorStateService.isLoading = false;
        this._notificationService.pushError(
          'Priority save failed'.trans('auditoriums/priority/save-priority/notifications/error.Title'),
          'An error occurred while saving the priority.'.trans('auditoriums/priority/save-priority/notifications/error.Message')
        );
        saved$.next(false);
        saved$.complete();
      }
    });

    return saved$.asObservable();
  }

  public saveZoneConfiguration(): Observable<boolean> {
    const saved$ = new Subject<boolean>();
    const pickedConfiguration = CreatorStateService.pickedConfiguration;

    if (!CreatorStateService.currentConfigurationName) {
      this._notificationService.pushError(
        'Error'.trans('auditoriums/save-configuration/no-name/notification/error.Title'),
        'The configuration name cannot be empty'.trans('auditoriums/save-configuration/no-name/notification/error.Message')
      );
      return of(false);
    }

    CreatorStateService.isLoading = true;
    CreatorCacheService.makeSnapshot();
    let apiCalls$: Observable<IDictionaryValue[]>[] = [];

    const parentSector = (Array.from(CreatorCacheService.seatsPlansCacheMap.values()) ?? []).find(x => x.seatsPlan.parentSectorId)?.seatsPlan;
    const seats: { seatDefinitionId: number, priceAreaId: number }[] = [];

    for (const [key, value] of CreatorCacheService.seatsPlansCacheMap) {
      const payload = this._creatorCurrentStepService.stepPayload<'zones'>(value.seatsPlan.id);

      if (parentSector) {
        seats.push(...payload.seats);
      } else {
        if (pickedConfiguration) {
          apiCalls$.push(this._sectorsConfigurationsApiService.updateConfiguration(key, pickedConfiguration.id, payload));
        } else {
          apiCalls$.push(this._sectorsConfigurationsApiService.saveConfiguration(key, payload));
        }
      }
    }

    if (parentSector) {
      const payload: IConfigurationPayload = {name: CreatorStateService.currentConfigurationName, seats};

      if (pickedConfiguration) {
        apiCalls$.push(this._sectorsConfigurationsApiService.updateConfiguration(parentSector.id, pickedConfiguration.id, payload));
      } else {
        apiCalls$.push(this._sectorsConfigurationsApiService.saveConfiguration(parentSector.id, payload));
      }
    }

    forkJoin(apiCalls$).pipe(
      take(1),
      finalize(() => CreatorStateService.isLoading = false),
    ).subscribe({
      next: () => {
        this._notificationService.pushSuccess(
          'Configuration saved'.trans('auditoriums/zone-configuration-details/save-configuration/notifications/success.Title'),
          'You have successfully saved the configuration.'.trans('auditoriums/zone-configuration-details/save-configuration/notifications/success.Message')
        );

        saved$.next(true);
        saved$.complete();
      },
      error: () => {
        CreatorStateService.isLoading = false;
        this._notificationService.pushError(
          'Configuration save failed'.trans('auditoriums/zone-configuration-details/save/notifications/error.Title'),
          'An error occurred while saving the configuration.'.trans('auditoriums/zone-configuration-details/save/notifications/error.Message')
        );
        saved$.next(false);
        saved$.complete();
      }
    });

    return saved$.asObservable();
  }
}
